import { animate, View, ViewOpts } from '@play-co/timestep-core/ui';
import Match2LiveOpsProgressbar from 'src/game/minigame/match2-timestep/components/Match2LiveOpsProgresbar';
import { waitForItPromise } from 'src/lib/utils';
import gsap from 'gsap';
import { match2BuruStreakRewards } from 'src/replicant/match2/match2.ruleset';
import { LangBitmapFontTextShadowView } from 'src/lib/ui/components/LangBitmapFontTextShadowView';
import { getStreakRewardIcon } from '../../match2.icons';
import {
  FlexBoxImageScaleView,
  FlexBoxView,
} from '@play-co/timestep-core/flexbox';

const assets = {
  bar: 'assets/ui/match2/common/bar_pinch_streak.png',
  inactive: 'assets/ui/match2/common/badge_pinch_streak.png',
  active: 'assets/ui/match2/common/badge_pinch_streak_active.png',
  cross: 'assets/ui/match2/common/icon_cross.png',
  tick: 'assets/ui/match2/common/icon_tick2.png',
};

type Match2BuruStreakLossBarOpts = ViewOpts & {
  currentProgress: number;
  maxProgress: number;
};

type Icon = { image: string; view: FlexBoxImageScaleView };

const barWidth = 610;
const barScale = 0.575;

export class Match2BuruStreakLossBar extends View {
  private progressBar: Match2LiveOpsProgressbar;
  private currentProgress = 0;
  private breakLoopAnimation = false;
  private icons: Icon[] = [];

  constructor(opts: Match2BuruStreakLossBarOpts) {
    super({
      width: 365,
      height: 80,
      ...opts,
    });

    this.currentProgress = Math.min(opts.currentProgress, opts.maxProgress);

    // Create the main progress bar
    this.progressBar = new Match2LiveOpsProgressbar({
      superview: this,
      width: barWidth,
      height: this.style.height,
      barWidth: barWidth,
      barHeight: 49,
      currentProgress: this.currentProgress,
      maxProgress: opts.maxProgress,
      labelOpts: { visible: false },
    });

    this.progressBar.updateOpts({
      scale: barScale,
      x: this.style.width * 0.5 - barWidth * barScale * 0.5,
      y: this.style.height * 0.5 - this.style.height * barScale * 0.5,
    });

    // Create badges and icons
    this.createElements();

    // Break the animation loop when the view is removed
    this.on('ViewRemoved', () => {
      this.breakLoopAnimation = true;
    });
  }

  private createElements() {
    // Create wrapper view for badges
    const badgesWrapper = new FlexBoxView({
      superview: this,
      flex: {
        position: 'absolute',
        width: this.style.width,
        height: this.style.height,
        justifyContent: 'space-between',
        alignItems: 'center',
        paddingLeft: 68,
      },
    });

    // Create wrapper view for tick/cross icons
    const iconsWrapper = new FlexBoxView({
      superview: this,
      flex: {
        position: 'absolute',
        width: this.style.width,
        height: this.style.height,
        justifyContent: 'flex-start',
        alignItems: 'center',
      },
    });

    for (let index = 0; index < match2BuruStreakRewards.length; index++) {
      const rewardSet = match2BuruStreakRewards[index];
      const size = 70;

      // Create badge background
      const badge = new FlexBoxImageScaleView({
        superview: badgesWrapper,
        image: assets.inactive,
        flex: {
          width: size,
          height: size,
          justifyContent: 'center',
          alignItems: 'center',
          marginRight: -3,
        },
      });

      // Create badge icon
      const badgeIcon = new FlexBoxImageScaleView({
        superview: badge,
        image: assets.active,
        scaleMethod: 'contain',
        align: 'center',
        verticalAlign: 'center',
        flex: {
          position: 'absolute',
          width: 53,
          height: 53,
          paddingBottom: 8,
        },
      });

      // Fetch and compose the reward icons
      getStreakRewardIcon(rewardSet)
        .get()
        .then((image) => {
          badgeIcon.image.setImage(image);
        });

      // Create count label
      new LangBitmapFontTextShadowView({
        superview: badge,
        centerAnchor: true,
        align: 'right',
        verticalAlign: 'bottom',
        size: 25,
        font: 'Title',
        width: size - 8,
        height: size - 21,
        textShadowOffsetX: 0,
        textShadowOffsetY: 2,
        textShadowOpacity: 0.35,
        localeText: () => `x${index + 1}`,
      });

      if (index < this.currentProgress) {
        // Create tick/cross icon
        this.icons.push({
          image: assets.tick,
          view: new FlexBoxImageScaleView({
            superview: iconsWrapper,
            image: assets.tick,
            scaleMethod: 'contain',
            align: 'center',
            verticalAlign: 'center',
            centerAnchor: true,
            flex: {
              width: 35,
              height: 35,
              marginTop: -22,
              marginLeft: 29,
              marginRight: 52,
            },
          }),
        });
      }
    }

    // Animate tick/cross icons' state according to bar progress
    this.progressBar.greenProgressBar.on('progress', (progress: number) => {
      this.icons.forEach((icon, index) => {
        const asset = progress >= index + 1 ? assets.tick : assets.cross;
        this.setIcon(icon, asset);
      });
    });
  }

  public animate() {
    this.loopAnimation();
  }

  private async loopAnimation() {
    if (this.breakLoopAnimation) return;
    this.icons.forEach((icon) => icon.view.image.setImage(assets.tick));
    this.progressBar.setProgress(this.currentProgress);
    await waitForItPromise(500);
    this.progressBar.greenProgressBar.animateProgress(0, 1500, animate.easeOut);
    await waitForItPromise(300);
    await this.progressBar.greyProgressBar.animateProgress(
      0,
      1500,
      animate.easeOut,
    );
    await waitForItPromise(1000);
    this.loopAnimation();
  }

  private setIcon(icon: Icon, asset: string, instant = false) {
    if (icon.image === asset) return;
    icon.image = asset;
    icon.view.image.setImage(asset);
    if (!instant) {
      gsap.fromTo(
        icon.view.style,
        { scale: 0 },
        { scale: 1, duration: 0.5, ease: 'back.out(2)', overwrite: true },
      );
    }
  }
}
